import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { ConnectionStatus, ConnectionStatusColorMap, DemandPartnerConnection, DemandPartnersConnectionRequest } from '../../domain/demand-partner-connection.model';
import { DemandPartnersService } from '../../services/demand-partners.service';
import { ServerErrorUtils } from '../../utils/server-error-utils';
import { SortUtils } from '../../utils/sort-utils';
import { OnDestroy } from '@angular/core';

@Component({
  selector: 'ctv-dp-connections-autocomplete',
  template: ` <mat-form-field
  [floatLabel]="floatLabel || 'always'"
  class="w-100"
  [ngClass]="formFieldClass"
  [appearance]="formFieldAppearance ? formFieldAppearance : undefined"
>
  <mat-label>{{label ? label : 'Select connection'}}</mat-label>
  <input
    matInput
    #search
    aria-label="DP Connection"
    [matAutocomplete]="auto"
    [formControl]="filterCtrl"
    [placeholder]="placeholder ? placeholder : 'Search'"
    [required]="required"
  />
  <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
  <mat-autocomplete
    #auto="matAutocomplete"
    (optionSelected)="optionSelected($event)"
    [displayWith]="display"
  >
    <mat-option *ngFor="let item of filteredItems | async" [value]="item">
      <i class="fas fa-circle status-circle small" ngClass="text-{{ConnectionStatusColorMap[item.status]}}"></i>
      {{ item.name }}
      <span *csHasNetworkPermission>
        <span *ngIf="item.network" class="badge badge-primary ml-3">N</span>
      </span>
    </mat-option>
  </mat-autocomplete>
</mat-form-field>`,
})
export class ConnectionsAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();

  @Input()
  public placeholder: string;

  @Input()
  public floatLabel: string;

  /** Hold list of networkIds to filter connections only by given networks.*/
  @Input() public networkIds: string[];

  @Input()
  public label: string;

  @Input()
  public formFieldClass: string;

  @Input()
  public formFieldAppearance: string;

  @Input()
  required: boolean;

  @Input()
  preselectedConnectionId: DemandPartnerConnection['id'];

  @Input()
  status: ConnectionStatus[];

  @Output()
  connectionSelected = new EventEmitter<DemandPartnerConnection>();

  filteredItems: Subject<DemandPartnerConnection[]>; //List of Connections to show in dropdown filtered with 'filteredConnectionIds'
  filterCtrl = new UntypedFormControl();
  connectionList: DemandPartnerConnection[] = []; //stores currently fetched list of connections
  selectedConnection: DemandPartnerConnection; //currently selected connection
  filteredConnectionIds: string[] = []; //It stores all the Id's of selected connections
  public loading = false;
  public ConnectionStatusColorMap = ConnectionStatusColorMap;

  constructor(
    private readonly service: DemandPartnersService,
    private readonly snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getPublishers(searchTerm);
          }
          else if (searchTerm == '') { //triggers when user deletes everything from input
            this.filteredItems.next();
            this.connectionSelected.emit(null);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedConnectionId) {
      this.getPublishers('', this.preselectedConnectionId);
    }
  }

  /** SearchTerm used for filtering by connection name. PreselectedConnectionId used to get only one connection (preselected) at the component initialization. */
  private getPublishers(searchTerm?: string, preselectedConnectionId?: string) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getAllConnections({
      page: 0,
      size: 1000,
      networkIds: this.networkIds,
      name: searchTerm,
      status: this.status,
      ids: [preselectedConnectionId]
    } as DemandPartnersConnectionRequest)
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedConnectionId ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected connection
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.connectionList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedConnectionId) {
            this.selectedConnection = this.connectionList[0]; //Naturally since the api call is filtered with one ID, selectedConnection will actually be the first one from the response
            this.filterCtrl.setValue(this.selectedConnection);
            this.updateFilteredConnections([this.selectedConnection]);
            this.emit();
          }
          this.filteredItems.next(this.connectionList.filter(x => !this.filteredConnectionIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching connection autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedConnection = $event.option.value;
    this.emit();
    this.searchFilter.nativeElement.blur(); //blur == unfocus
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component.
   * If wrapped inside chips autocomplete it emits value to chips component that forwards values to parent component. */
  private emit() {
    this.connectionSelected.emit(this.selectedConnection);
  }

  /** Trigger only when using chips autocomplete. Triggered when item selected through chips autocomplete to update filteredConnectionIds. */
  public updateFilteredConnections(items) {
    this.filteredConnectionIds = items.map((item) => item.id);
  }

  /** Returns wanted object to input from whole selected item model. */
  public display(p?: DemandPartnerConnection) {
    return p ? p.name : undefined;
  }

  /** Used only by chips autocomplete component to clear the input from selected item*/
  public resetInput() {
    this.filterCtrl.setValue(null);
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty
  }

  public disable() {
    this.filterCtrl.disable();
  }

  public enable() {
    this.filterCtrl.enable();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
